import React from 'react';
import { useVolverArribaAlCambiar } from '../../hooks/useVolverArriba';

export interface SegmentOption<T extends string> {
    value: T;
    label: string;
    /** Contador opcional a la derecha de la etiqueta */
    count?: number;
}

interface SegmentedControlProps<T extends string> {
    options: SegmentOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /** 'pill' = fondo blanco deslizante (filtros) · 'underline' = pestañas de sección */
    variant?: 'pill' | 'underline';
    className?: string;
}

/**
 * SegmentedControl — Sistema 2.0.
 * - `pill`: control compacto sobre acero-100 para filtros de una vista.
 * - `underline`: pestañas de sección con subrayado amarillo (mismo lenguaje
 *   que los meses del dashboard). Ambas variantes soportan contadores.
 */
export function SegmentedControl<T extends string>({
    options, value, onChange, variant = 'pill', className = '',
}: SegmentedControlProps<T>) {
    /* Cambiar de PESTAÑA devuelve la vista arriba; cambiar de FILTRO no.
     *
     * Sin esto, la pestaña nueva se pinta con el scroll de la anterior: bajas
     * en «Legal y avisos», pulsas «Pagos» y entras a una pantalla en la que no
     * has estado nunca, ya scrolleada hasta el final. Se arregla aquí porque
     * este componente es el que dibuja las pestañas de sección de toda la app.
     *
     * Un filtro (`pill`) es lo contrario: estrecha la lista que ya tienes
     * delante, y saltar arriba en medio de una comparación estorba. Por eso la
     * clave solo cambia en `underline` — en `pill` es siempre `undefined` y el
     * efecto no llega a dispararse.
     *
     * El riel se pasa como ancla para acertar con la caja correcta cuando las
     * pestañas viven dentro de un panel con scroll propio (ClientDetail, el
     * panel de la propuesta): ahí lo que vuelve a cero es ese panel.
     */
    const riel = React.useRef<HTMLDivElement>(null);
    useVolverArribaAlCambiar(variant === 'underline' ? value : undefined, riel);

    if (variant === 'underline') {
        return (
            // flex-wrap, not overflow scroll. A section with many tabs (Service
            // Routes has eight) used to run off the right edge of a phone, and
            // tabs you cannot see are tabs nobody opens — Equipment and
            // Scheduler were effectively hidden. Wrapping onto a second row
            // keeps every tab reachable without a swipe. On wide screens they
            // fit one row anyway, so nothing changes there.
            //
            // `min-w-0` es aparte y sigue haciendo falta: esta tira se usa con
            // `flex-1` al lado del botón de ayuda, y un ítem flex trae
            // `min-width: auto`, o sea que no baja del ancho de su contenido por
            // mucho `flex-1` que lleve. Con una sola pestaña de nombre largo
            // —que no puede envolver, son `shrink-0`— volvía a desbordarse sobre
            // el vecino. Se veía en Estimados: «Firmados» encimado con «How to
            // use».
            <div
                ref={riel}
                className={`flex flex-wrap items-center gap-x-4 sm:gap-x-5 gap-y-1 border-b border-acero-200 min-w-0 ${className}`}
                role="tablist"
            >
                {options.map(opt => {
                    const active = opt.value === value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => onChange(opt.value)}
                            className={`relative pb-2.5 text-[13px] sm:text-sm shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-t ${
                                active ? 'text-navy-950 font-semibold' : 'text-acero-500 hover:text-navy-950 font-medium'
                            }`}
                        >
                            {opt.label}
                            {typeof opt.count === 'number' && (
                                <span className={`ml-1.5 font-data text-xs tabular-nums ${active ? 'text-acero-500' : 'text-acero-400'}`}>
                                    {opt.count}
                                </span>
                            )}
                            {active && (
                                <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-brand-400" aria-hidden="true" />
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={`inline-flex items-center gap-0.5 bg-acero-100 rounded-xl p-0.5 h-10 ${className}`} role="tablist">
            {options.map(opt => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(opt.value)}
                        className={`h-9 px-3.5 rounded-[10px] text-[13px] font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                            active
                                ? 'bg-white text-navy-950 font-semibold shadow-s1'
                                : 'text-acero-600 hover:text-navy-950'
                        }`}
                    >
                        {opt.label}
                        {typeof opt.count === 'number' && (
                            <span className={`ml-1.5 font-data text-xs tabular-nums ${active ? 'text-acero-500' : 'text-acero-400'}`}>
                                {opt.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
